<template>
  <v-dialog
    :model-value="modelValue"
    class="us-team-dialog-root"
    content-class="us-team-dialog-root"
    max-width="680"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card us-team-dialog">
      <div class="dialog-header us-team-dialog__header">
        <div class="us-team-dialog__header-main">
          <div class="dialog-header__icon us-team-dialog__icon-wrap" aria-hidden="true">
            <v-icon icon="mdi-account-plus-outline" size="22" />
          </div>
          <div class="us-team-dialog__head-copy">
            <span class="dialog-header__title">Add Admin/Agent</span>
            <span class="us-team-dialog__subtitle">
              Invite an admin, or add an AI or human agent.
            </span>
          </div>
        </div>
        <v-btn
          icon
          variant="flat"
          size="small"
          class="dialog-close-btn us-team-dialog__close"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body us-team-dialog__body">
        <v-form ref="formRef" v-model="isValid" @submit.prevent="submit">
          <AgentTypePicker
            v-model="form.role_type"
            :options="roleOptions"
            label="Role type"
            hint="Admins manage the portal; agents handle conversations."
            :disabled="saving"
          />

          <AgentTypePicker
            v-if="isAgentRoleSelected"
            v-model="form.agent_type"
            label="Agent type"
            :disabled="saving"
          />

          <section class="us-team-dialog__section">
            <h3 v-if="!isAiAgentSelected" class="us-team-dialog__section-title">
              Profile
            </h3>

            <div v-if="isAiAgentSelected" class="us-team-dialog__field">
              <span class="us-team-dialog__label">Agent name</span>
              <v-text-field
                v-model="form.user_name"
                placeholder="e.g. Sales AI"
                prepend-inner-icon="mdi-account-outline"
                variant="outlined"
                density="comfortable"
                color="primary"
                hide-details="auto"
                :rules="nameRules"
                class="us-team-dialog__input"
              />
            </div>

            <div v-if="isAiAgentSelected" class="us-team-dialog__field-row">
              <label class="us-team-dialog__field us-team-dialog__field--half">
                <span class="us-team-dialog__label">Voice gender</span>
                <v-select
                  v-model="form.agent_gender"
                  :items="genderOptions"
                  item-title="label"
                  item-value="value"
                  placeholder="Select gender"
                  prepend-inner-icon="mdi-gender-male-female"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  class="us-team-dialog__input"
                />
              </label>

              <label class="us-team-dialog__field us-team-dialog__field--half">
                <span class="us-team-dialog__label">Voice language</span>
                <v-select
                  v-model="form.agent_language"
                  :items="languageOptions"
                  item-title="label"
                  item-value="value"
                  placeholder="Select language"
                  prepend-inner-icon="mdi-translate"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  class="us-team-dialog__input"
                />
              </label>
            </div>
            <p v-if="isAiAgentSelected" class="us-team-dialog__hint">
              This agent speaks with its own voice on calls it handles — defaults to your organisation's language, but can be set independently per agent.
            </p>

            <div v-else class="us-team-dialog__field-row">
              <label class="us-team-dialog__field us-team-dialog__field--half">
                <span class="us-team-dialog__label">Full name</span>
                <v-text-field
                  v-model="form.user_name"
                  placeholder="Jane Smith"
                  prepend-inner-icon="mdi-account-outline"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  :rules="nameRules"
                  class="us-team-dialog__input"
                />
              </label>

              <label class="us-team-dialog__field us-team-dialog__field--half">
                <span class="us-team-dialog__label">Work email</span>
                <v-text-field
                  v-model="form.user_email_id"
                  placeholder="name@company.com"
                  type="email"
                  prepend-inner-icon="mdi-email-outline"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  :rules="emailRules"
                  class="us-team-dialog__input"
                />
              </label>
            </div>
          </section>

          <section v-if="!isAiAgentSelected" class="us-team-dialog__section">
            <h3 class="us-team-dialog__section-title">Contact</h3>

            <label class="us-team-dialog__field">
              <span class="us-team-dialog__label">Mobile number</span>
              <div class="us-team-dialog__phone-row">
                <v-autocomplete
                  v-model="form.country_code"
                  :items="countryOptions"
                  item-title="label"
                  item-value="dial_code"
                  placeholder="+91"
                  prepend-inner-icon="mdi-earth"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  :rules="countryCodeRules"
                  class="us-team-dialog__input us-team-dialog__phone-code"
                  menu-icon="mdi-chevron-down"
                />
                <v-text-field
                  v-model="form.mobile_number"
                  placeholder="10-digit number"
                  prepend-inner-icon="mdi-phone-outline"
                  variant="outlined"
                  density="comfortable"
                  color="primary"
                  hide-details="auto"
                  maxlength="10"
                  inputmode="numeric"
                  :rules="mobileNumberRules"
                  class="us-team-dialog__input"
                  @update:model-value="onMobileNumberInput"
                />
              </div>
            </label>
          </section>

          <section v-if="!isAiAgentSelected" class="us-team-dialog__section">
            <h3 class="us-team-dialog__section-title">Business hours</h3>
            <p class="us-team-dialog__hint">
              When this person is a CALL_REDIRECT target, they're only
              reachable within these hours. Leave blank to use the org's own
              hours ({{ orgBusinessHoursLabel }}).
            </p>
            <div class="us-team-dialog__phone-row">
              <v-text-field
                v-model="form.business_hours_start"
                type="time"
                label="Start"
                variant="outlined"
                density="comfortable"
                color="primary"
                hide-details="auto"
                class="us-team-dialog__input"
              />
              <v-text-field
                v-model="form.business_hours_end"
                type="time"
                label="End"
                variant="outlined"
                density="comfortable"
                color="primary"
                hide-details="auto"
                class="us-team-dialog__input"
              />
            </div>
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="dialog-footer us-team-dialog__footer">
        <v-spacer />
        <v-btn
          variant="flat"
          class="text-none us-team-dialog__cancel"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          class="text-none btn-primary-cta us-team-dialog__save"
          :loading="saving"
          :disabled="!canSave"
          rounded="lg"
          @click="submit"
        >
          Add Admin/Agent
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import orgUsersApi from '@/mixins/Users/orgUsersApi'
import AgentTypePicker from '@/components/Users/AgentTypePicker.vue'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import countryCodeList from '@/JsonFiles/CountryCodeList.json'
import {
  DEFAULT_COUNTRY_DIAL_CODE,
  isValidMobile10,
  normalizeMobileDigits,
} from '@/utils/agentConfiguration'
import {
  AGENT_TYPE,
  DEFAULT_AGENT_TYPE,
  DEFAULT_USER_ROLE,
  USER_ROLE,
  USER_ROLE_OPTIONS,
  isAiAgent,
  validateCreateOrgUserForm,
} from '@/utils/orgUsers'
import {
  CHATBOT_GENDER_OPTIONS,
  getAgentLanguageOptionsForOrg,
  parseChatbotDetails,
} from '@/utils/agentOtherSettings'
import '@/styles/users/team-form-dialog.css'

const defaultForm = () => ({
  user_email_id: '',
  user_name: '',
  role_type: DEFAULT_USER_ROLE,
  agent_type: DEFAULT_AGENT_TYPE,
  country_code: DEFAULT_COUNTRY_DIAL_CODE,
  mobile_number: '',
  business_hours_start: '',
  business_hours_end: '',
  agent_gender: '',
  agent_language: '',
})

export default {
  name: 'CreateUserDialog',
  components: { AgentTypePicker },
  mixins: [orgUsersApi],
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    // Agents (AI or human) only exist to be assigned to outbound
    // campaigns/tasks -- when the org has outgoing disabled, adding one
    // would create a user with nothing to do, so the Agent role option
    // is hidden and only Admin can be added.
    outgoingEnabled: {
      type: Boolean,
      default: true,
    },
    // The org's current Admins/Agents roster (whatever UsersPage.vue already
    // has loaded) -- checked client-side so a duplicate name is caught while
    // typing instead of only after a round trip to CreateOrgUser's own copy
    // of this check.
    existingUsers: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'saved', 'error'],
  data() {
    return {
      form: defaultForm(),
      countryOptions: countryCodeList.map((country) => ({
        ...country,
        label: `${country.dial_code} · ${country.name}`,
      })),
      isValid: false,
      saving: false,
    }
  },
  computed: {
    orgBusinessHoursLabel() {
      const org = this.$store.state.organization || {}
      return `${org.business_hours_start || '10:00'}–${org.business_hours_end || '19:00'}`
    },
    roleOptions() {
      if (this.outgoingEnabled) return USER_ROLE_OPTIONS
      return USER_ROLE_OPTIONS.filter((option) => option.value !== USER_ROLE.AGENT)
    },
    isAgentRoleSelected() {
      return this.form.role_type === USER_ROLE.AGENT
    },
    /** Admins always follow the human flow (email + phone). */
    effectiveAgentType() {
      return this.isAgentRoleSelected ? this.form.agent_type : AGENT_TYPE.HUMAN
    },
    isAiAgentSelected() {
      return this.isAgentRoleSelected && isAiAgent(this.form.agent_type)
    },
    org() {
      return this.$store.state.organization || {}
    },
    genderOptions() {
      return CHATBOT_GENDER_OPTIONS
    },
    languageOptions() {
      return getAgentLanguageOptionsForOrg(this.org)
    },
    /** What a brand-new AI agent's voice defaults to -- the org's own
     * chatbot_details, same source the Organisation Settings language/
     * gender pickers themselves read from. */
    orgDefaultGender() {
      return parseChatbotDetails(this.org.chatbot_details).chatbot_gender || this.org.chatbot_gender || ''
    },
    orgDefaultLanguage() {
      return parseChatbotDetails(this.org.chatbot_details).chatbot_language || this.org.chatbot_language || ''
    },
    nameRules() {
      return [
        (v) => !!String(v ?? '').trim() || 'Name is required',
        (v) => {
          const normalized = String(v ?? '').trim().toLowerCase()
          if (!normalized) return true
          const collision = this.existingUsers.some(
            (u) => String(u.name ?? '').trim().toLowerCase() === normalized,
          )
          return !collision || 'Someone in this organisation already uses that name'
        },
      ]
    },
    emailRules() {
      if (this.isAiAgentSelected) return []
      return [
        (v) => !!String(v ?? '').trim() || 'Email is required',
        (v) => /.+@.+\..+/.test(String(v ?? '')) || 'Enter a valid email',
      ]
    },
    countryCodeRules() {
      if (this.isAiAgentSelected) return []
      return [(v) => !!String(v ?? '').trim() || 'Country code is required']
    },
    mobileNumberRules() {
      if (this.isAiAgentSelected) return []
      return [
        (v) => !!String(v ?? '').trim() || 'Mobile number is required',
        (v) => isValidMobile10(v) || 'Enter a valid 10-digit number',
      ]
    },
    canSave() {
      return validateCreateOrgUserForm({
        ...this.form,
        agent_type: this.effectiveAgentType,
        mobile_number: normalizeMobileDigits(this.form.mobile_number),
      }).valid
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.form = defaultForm()
        this.$nextTick(() => this.$refs.formRef?.resetValidation())
      }
    },
    'form.role_type'() {
      this.form.agent_type = DEFAULT_AGENT_TYPE
      this.$nextTick(() => this.$refs.formRef?.resetValidation())
    },
    'form.agent_type'(next) {
      if (this.isAgentRoleSelected && isAiAgent(next)) {
        this.form.user_email_id = ''
        this.form.mobile_number = ''
        // Pre-populate from the organisation's own voice defaults -- an
        // admin adding a new AI agent almost always wants it to match the
        // org's usual voice unless they deliberately pick something else,
        // and only default what's still blank so re-selecting AI Agent
        // after already choosing something doesn't clobber it.
        if (!this.form.agent_gender) this.form.agent_gender = this.orgDefaultGender
        if (!this.form.agent_language) this.form.agent_language = this.orgDefaultLanguage
      }
      this.$nextTick(() => this.$refs.formRef?.resetValidation())
    },
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    onMobileNumberInput(value) {
      this.form.mobile_number = normalizeMobileDigits(value)
    },
    async submit() {
      const formValidation = await this.$refs.formRef?.validate()
      if (formValidation === false || formValidation?.valid === false) return
      if (!this.canSave) return

      this.saving = true
      const response = await this.createOrgUser({
        user_name: this.form.user_name.trim(),
        user_role: this.form.role_type,
        agent_type: this.effectiveAgentType,
        user_email_id: this.form.user_email_id,
        country_code: this.form.country_code,
        mobile_number: normalizeMobileDigits(this.form.mobile_number),
        business_hours_start: this.form.business_hours_start || null,
        business_hours_end: this.form.business_hours_end || null,
        agent_gender: this.isAiAgentSelected ? (this.form.agent_gender || null) : null,
        agent_language: this.isAiAgentSelected ? (this.form.agent_language || null) : null,
      })
      this.saving = false

      if (isApiSuccess(response)) {
        this.$emit('saved', response)
        this.close()
      } else {
        this.$emit('error', response)
      }
    },
  },
}
</script>
